import { useMemo } from "react";
import { MedalMilitary } from "@phosphor-icons/react";

/**
 * Ranking general de toda la reta (suma todas las canchas + todas las rondas + todos los pizarrones + planillas).
 */
export default function RetaGeneralRanking({ reta, testIdPrefix }) {
  const ranking = useMemo(() => {
    const map = new Map();
    (reta.courts || []).forEach((c) => {
      let sbs = c.scoreboards || [];
      if (!sbs.length && c.scoreboard) sbs = [c.scoreboard];
      sbs.forEach((sb) => {
        const lu = sb.lineup || {};
        const sa = Number(sb.score_a) || 0;
        const sb_score = Number(sb.score_b) || 0;
        const winA = sa > sb_score;
        const winB = sb_score > sa;
        const add = (slot, pts, didWin) => {
          const p = lu[slot];
          if (!p) return;
          const k = p.user_id || p.name;
          if (!k) return;
          const e = map.get(k) || { key: k, name: p.name, is_guest: !!p.is_guest, total: 0, retas: 0, wins: 0, courts: new Set(), planilla: 0 };
          e.total += pts;
          e.retas += 1;
          if (didWin) e.wins += 1;
          e.courts.add(c.name);
          map.set(k, e);
        };
        add("a1", sa, winA);
        add("a2", sa, winA);
        add("b1", sb_score, winB);
        add("b2", sb_score, winB);
      });
      // Merge planilla totals per court
      const pl = c.planilla;
      (pl?.players || []).forEach((p) => {
        const k = p.user_id || p.name;
        if (!k || (!p.name && !p.user_id)) return;
        const t = Number(p.total) || 0;
        if (t <= 0) return;
        const e = map.get(k) || { key: k, name: p.name, is_guest: !!p.is_guest, total: 0, retas: 0, wins: 0, courts: new Set(), planilla: 0 };
        e.total += t;
        e.planilla += t;
        e.courts.add(c.name);
        map.set(k, e);
      });
    });
    return Array.from(map.values()).map((e) => ({ ...e, courts_played: e.courts.size })).sort((a, b) => b.total - a.total || b.wins - a.wins);
  }, [reta]);

  if (!ranking.length) return null;

  return (
    <div className="mt-4 p-4 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 border border-amber-500/30 rounded-md" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <MedalMilitary size={18} weight="fill" className="text-amber-400" />
        <span className="text-[11px] tracking-[0.25em] uppercase font-bold text-amber-400">Ranking General de la Reta</span>
        <span className="ml-auto text-[10px] text-gray-400">{(reta.courts || []).length} canchas · {ranking.length} jugadores</span>
      </div>
      <div className="space-y-1">
        {ranking.slice(0, 10).map((p, i) => {
          const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : null;
          const bg = i === 0 ? "bg-amber-400/20 border-amber-400/40" : i === 1 ? "bg-gray-400/20 border-gray-400/40" : i === 2 ? "bg-orange-700/20 border-orange-700/40" : "bg-white/[0.03] border-white/10";
          return (
            <div key={p.key} className={`flex items-center justify-between px-3 py-2 border rounded ${bg}`} data-testid={`${testIdPrefix}-row-${i}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-sm font-mono text-gray-300 w-7 text-center shrink-0">{medal || `#${i + 1}`}</span>
                <span className="text-sm text-white truncate font-medium" title={p.name}>{p.name}{p.is_guest ? " (inv)" : ""}</span>
                {p.planilla > 0 && <span className="text-[9px] text-amber-300/80 font-mono shrink-0" title="Puntos de planilla">+{p.planilla}P</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[9px] text-gray-500 uppercase">{p.courts_played}c · {p.wins}V · {p.retas}j</span>
                <span className="font-['Barlow_Condensed'] text-2xl font-black text-amber-400 leading-none" data-testid={`${testIdPrefix}-pts-${i}`}>{p.total}</span>
                <span className="text-[9px] text-gray-500 uppercase">pts</span>
              </div>
            </div>
          );
        })}
        {ranking.length > 10 && (
          <div className="text-[10px] text-gray-500 italic text-center pt-1">y {ranking.length - 10} jugadores más</div>
        )}
      </div>
    </div>
  );
}
